/**
 * Tekst en pictogrammen in de hal: locatielabels, verzendetiketten,
 * gangpadborden, draagvermogenplaten en veiligheidsborden.
 *
 * ## Waarom dit het verschil maakt
 *
 * Een ruimte zonder tekst leest als een maquette. Het oog zoekt in elk beeld
 * naar leesbare tekens, en een magazijn staat er in het echt vol mee: elk
 * pallethok heeft een locatiecode met barcode, elke doos een verzendlabel,
 * elke stellingkop een draagvermogenplaat. Je leest ze niet, maar je ziet dat
 * ze er zijn — en dat ze allemaal net anders zijn.
 *
 * Alles wordt bij het laden op een canvas getekend: geen afbeeldingen om mee
 * te leveren, dus niets extra's dat offline op de kiosk moet staan. Het
 * lettertype is Inter als het al geladen is en anders de systeemschreefloze;
 * op de afstand waarop je deze labels ziet is dat verschil er niet.
 */
import * as THREE from 'three'
import { prng } from './indeling'

const LETTER = 'Inter, "Helvetica Neue", Arial, sans-serif'

function canvas(b: number, h: number): [HTMLCanvasElement, CanvasRenderingContext2D] {
  const c = document.createElement('canvas')
  c.width = b
  c.height = h
  return [c, c.getContext('2d')!]
}

function textuur(c: HTMLCanvasElement) {
  const t = new THREE.CanvasTexture(c)
  t.colorSpace = THREE.SRGBColorSpace
  t.anisotropy = 8
  t.generateMipmaps = true
  t.minFilter = THREE.LinearMipmapLinearFilter
  return t
}

/** Code 128-achtige barcode: willekeurig, maar met de ritmiek van een echte. */
function barcode(g: CanvasRenderingContext2D, x: number, y: number, b: number, h: number, rnd: () => number) {
  let px = x
  // Start- en stopbalken zijn in het echt vast; dat geeft de herkenbare rand.
  const breedtes = [2, 1, 1, 2, 3, 1]
  while (px < x + b - 8) {
    const w = (breedtes[Math.floor(rnd() * breedtes.length)] * b) / 110
    if (rnd() > 0.45) g.fillRect(px, y, w, h)
    px += w
  }
}

/**
 * Atlas met locatielabels: 8 × 8 verschillende codes. Elk label is wit met
 * zwarte code, een barcode en een pijl naar het niveau — de standaard in een
 * palletmagazijn. De stellingen kiezen per ligger een vak uit deze atlas.
 */
export const ATLAS_KOLOMMEN = 8
export function locatieAtlas(seed: number) {
  const cel = { b: 160, h: 80 }
  const [c, g] = canvas(cel.b * ATLAS_KOLOMMEN, cel.h * ATLAS_KOLOMMEN)
  const rnd = prng(seed)
  g.fillStyle = '#1b1d20'
  g.fillRect(0, 0, c.width, c.height)
  for (let r = 0; r < ATLAS_KOLOMMEN; r++) {
    for (let k = 0; k < ATLAS_KOLOMMEN; k++) {
      const x = k * cel.b
      const y = r * cel.h
      // Label, iets vergeeld, met een smalle marge van de ligger eromheen.
      const geel = 238 - Math.floor(rnd() * 22)
      g.fillStyle = `rgb(${geel},${geel},${geel - 6})`
      g.fillRect(x + 3, y + 4, cel.b - 6, cel.h - 8)
      // Kleurbalk per niveau: zo staan ze in het echt, en het geeft rijen.
      const kleuren = ['#d8515e', '#f5b301', '#2e9bff', '#57b888']
      g.fillStyle = kleuren[r % 4]
      g.fillRect(x + 3, y + 4, 14, cel.h - 8)
      g.fillStyle = '#111'
      g.font = `700 26px ${LETTER}`
      g.textBaseline = 'top'
      const gang = String.fromCharCode(65 + (r % 6))
      const vak = String(1 + ((k * 7 + r * 3) % 40)).padStart(2, '0')
      g.fillText(`${gang}-${vak}-${(r % 5) + 1}`, x + 24, y + 10)
      barcode(g, x + 24, y + 44, cel.b - 50, 24, rnd)
      // Pijl omhoog of omlaag, naar het vak waar het label bij hoort.
      g.beginPath()
      const px = x + cel.b - 18
      const omhoog = r % 2 === 0
      g.moveTo(px - 7, omhoog ? y + 60 : y + 46)
      g.lineTo(px + 7, omhoog ? y + 60 : y + 46)
      g.lineTo(px, omhoog ? y + 46 : y + 60)
      g.fill()
      // Een krasje of vuile vinger, op een deel van de labels.
      if (rnd() > 0.7) {
        g.fillStyle = 'rgba(60,50,40,0.18)'
        g.beginPath()
        g.ellipse(x + 30 + rnd() * 100, y + 20 + rnd() * 40, 10 + rnd() * 14, 5 + rnd() * 6, rnd() * 3, 0, Math.PI * 2)
        g.fill()
      }
    }
  }
  return textuur(c)
}

/** Verzendlabel: logo-blok, adresregels, een grote barcode en een sorteercode. */
export function verzendlabel(seed: number) {
  const [c, g] = canvas(256, 192)
  const rnd = prng(seed)
  g.fillStyle = '#f4f3ee'
  g.fillRect(0, 0, 256, 192)
  g.fillStyle = '#141414'
  g.fillRect(10, 10, 58, 30)
  g.fillStyle = '#f4f3ee'
  g.font = `800 18px ${LETTER}`
  g.fillText('NL', 24, 32)
  g.fillStyle = '#222'
  for (let i = 0; i < 4; i++) g.fillRect(80, 12 + i * 8, 90 + rnd() * 70, 4)
  g.lineWidth = 2
  g.strokeStyle = '#222'
  g.strokeRect(6, 50, 244, 50)
  g.font = `700 30px ${LETTER}`
  g.fillStyle = '#111'
  g.fillText(`${Math.floor(1000 + rnd() * 8999)} ${String.fromCharCode(65 + Math.floor(rnd() * 26))}${String.fromCharCode(65 + Math.floor(rnd() * 26))}`, 14, 88)
  barcode(g, 12, 110, 232, 58, rnd)
  g.font = `500 12px ${LETTER}`
  g.fillText('00 3 87654 32109 87654 3', 30, 184)
  return textuur(c)
}

/** Gangpadbord aan het plafond: blauw, wit cijfer, dubbelzijdig bruikbaar. */
export function gangpadBord(nummer: number) {
  const [c, g] = canvas(384, 160)
  g.fillStyle = '#1d4f8c'
  g.fillRect(0, 0, 384, 160)
  g.strokeStyle = '#f2f5f8'
  g.lineWidth = 6
  g.strokeRect(10, 10, 364, 140)
  g.fillStyle = '#f2f5f8'
  g.font = `600 30px ${LETTER}`
  g.textBaseline = 'middle'
  g.fillText('GANG', 32, 80)
  g.font = `800 104px ${LETTER}`
  g.textAlign = 'right'
  g.fillText(String(nummer).padStart(2, '0'), 356, 86)
  return textuur(c)
}

/** Draagvermogenplaat op de stellingkop, verplicht volgens EN 15635. */
export function draagvermogen() {
  const [c, g] = canvas(192, 256)
  g.fillStyle = '#f5b301'
  g.fillRect(0, 0, 192, 256)
  g.fillStyle = '#141414'
  g.fillRect(8, 8, 176, 40)
  g.fillStyle = '#f5b301'
  g.font = `800 22px ${LETTER}`
  g.textAlign = 'center'
  g.textBaseline = 'middle'
  g.fillText('MAX. LAST', 96, 29)
  g.fillStyle = '#141414'
  // Schets van de stelling met de last per niveau.
  g.lineWidth = 4
  g.strokeRect(36, 64, 120, 130)
  for (let i = 1; i < 4; i++) { g.beginPath(); g.moveTo(36, 64 + i * 32.5); g.lineTo(156, 64 + i * 32.5); g.stroke() }
  g.font = `700 15px ${LETTER}`
  for (let i = 0; i < 4; i++) g.fillText('1000 kg', 96, 80 + i * 32.5)
  g.font = `800 20px ${LETTER}`
  g.fillText('∑ 4000 kg', 96, 224)
  return textuur(c)
}

/** Nooduitgang: groen, met de rennende figuur en een pijl (ISO 7010 E002). */
export function nooduitgang() {
  const [c, g] = canvas(320, 144)
  g.fillStyle = '#0d8a4f'
  g.fillRect(0, 0, 320, 144)
  g.fillStyle = '#f4fff8'
  // Deur
  g.fillRect(28, 22, 58, 100)
  g.fillStyle = '#0d8a4f'
  g.fillRect(36, 30, 42, 92)
  g.fillStyle = '#f4fff8'
  // Rennende figuur, in een paar streken
  g.beginPath(); g.arc(128, 36, 12, 0, Math.PI * 2); g.fill()
  g.lineWidth = 12; g.lineCap = 'round'; g.strokeStyle = '#f4fff8'
  g.beginPath(); g.moveTo(124, 54); g.lineTo(112, 86); g.lineTo(92, 112); g.stroke()
  g.beginPath(); g.moveTo(112, 86); g.lineTo(138, 100); g.lineTo(146, 122); g.stroke()
  g.beginPath(); g.moveTo(122, 60); g.lineTo(146, 72); g.lineTo(160, 60); g.stroke()
  g.beginPath(); g.moveTo(120, 62); g.lineTo(100, 74); g.stroke()
  // Pijl
  g.beginPath()
  g.moveTo(186, 60); g.lineTo(250, 60); g.lineTo(250, 40); g.lineTo(296, 72); g.lineTo(250, 104); g.lineTo(250, 84); g.lineTo(186, 84)
  g.closePath(); g.fill()
  return textuur(c)
}

export type Bordsoort = 'heftruck' | 'hesje' | 'rookverbod' | 'gehoor'

/** Veiligheidsborden aan de wand. Vereenvoudigde pictogrammen, echte kleuren. */
export function veiligheidsbord(soort: Bordsoort) {
  const [c, g] = canvas(256, 320)
  g.fillStyle = '#f2f2ee'
  g.fillRect(0, 0, 256, 320)
  g.textAlign = 'center'
  g.textBaseline = 'middle'
  if (soort === 'heftruck') {
    g.fillStyle = '#f5b301'
    g.beginPath(); g.moveTo(128, 18); g.lineTo(240, 212); g.lineTo(16, 212); g.closePath(); g.fill()
    g.lineWidth = 10; g.strokeStyle = '#141414'; g.lineJoin = 'round'; g.stroke()
    g.fillStyle = '#141414'
    // Heftruck in silhouet: cabine, mast, vork, twee wielen.
    g.fillRect(84, 128, 58, 42)
    g.fillRect(142, 92, 10, 80)
    g.fillRect(152, 160, 36, 8)
    g.fillRect(96, 104, 30, 26)
    g.beginPath(); g.arc(98, 176, 13, 0, Math.PI * 2); g.arc(136, 176, 13, 0, Math.PI * 2); g.fill()
    g.font = `800 28px ${LETTER}`
    g.fillText('LET OP', 128, 250)
    g.font = `700 24px ${LETTER}`
    g.fillText('HEFTRUCKS', 128, 284)
  } else {
    const kleur = soort === 'rookverbod' ? '#c8102e' : '#1d5fa8'
    if (soort === 'rookverbod') {
      g.lineWidth = 22; g.strokeStyle = kleur
      g.beginPath(); g.arc(128, 116, 88, 0, Math.PI * 2); g.stroke()
      g.fillStyle = '#141414'; g.fillRect(64, 108, 118, 18)
      g.beginPath(); g.moveTo(66, 60); g.lineTo(190, 172); g.stroke()
    } else {
      g.fillStyle = kleur
      g.beginPath(); g.arc(128, 116, 96, 0, Math.PI * 2); g.fill()
      g.fillStyle = '#f2f2ee'
      if (soort === 'hesje') {
        g.beginPath(); g.moveTo(96, 56); g.lineTo(160, 56); g.lineTo(176, 176); g.lineTo(80, 176); g.closePath(); g.fill()
        g.fillStyle = kleur; g.fillRect(84, 120, 88, 12); g.fillRect(86, 146, 84, 12)
      } else {
        g.beginPath(); g.arc(128, 116, 34, 0, Math.PI * 2); g.fill()
        g.lineWidth = 12; g.strokeStyle = '#f2f2ee'
        g.beginPath(); g.arc(128, 104, 58, Math.PI * 1.05, Math.PI * 1.95); g.stroke()
        g.fillRect(64, 96, 22, 44); g.fillRect(170, 96, 22, 44)
      }
    }
    g.fillStyle = '#141414'
    g.font = `800 25px ${LETTER}`
    const tekst = { hesje: ['HESJE', 'VERPLICHT'], rookverbod: ['VERBODEN', 'TE ROKEN'], gehoor: ['GEHOOR-', 'BESCHERMING'] }[soort]
    g.fillText(tekst[0], 128, 250)
    g.font = `700 22px ${LETTER}`
    g.fillText(tekst[1], 128, 282)
  }
  return textuur(c)
}
